import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, X, AlertCircle } from "lucide-react";
import { useState } from "react";

export default function ApprovalModal({ isOpen, onClose, policy, onApprove }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // console.log("POLICY_DETAILS: ", policy);

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      await onApprove(policy.slug);
      onClose();
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 backdrop-blur-sm bg-black/50"
          />

          {/* Modal */}
          <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-3 right-3 p-1.5 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100 z-10"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Content */}
              <div className="p-4 sm:p-5">
                {/* Icon */}
                <div className="flex justify-center mb-3">
                  <div className="p-2.5 bg-green-100 rounded-full">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                </div>

                {/* Title */}
                <h2 className="mb-1.5 text-xl sm:text-2xl font-bold text-center text-gray-900">
                  Approve Policy
                </h2>

                {/* Description */}
                <p className="mb-4 text-sm sm:text-base text-center text-gray-600">
                  Are you sure you want to approve this insurance policy?
                </p>

                {/* Policy Info */}
                <div className="p-3 mb-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="mb-2">
                    <p className="text-xs text-gray-500">Policy Name</p>
                    <p className="text-sm font-semibold text-gray-900 line-clamp-1">{policy?.policy_name}</p>
                  </div>
                  <div className="mb-2">
                    <p className="text-xs text-gray-500">Provider</p>
                    <p className="text-sm font-medium text-gray-900">{policy?.insurance_provider?.company_name || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Type</p>
                    <p className="text-sm font-medium text-gray-900 capitalize">
                      {policy?.policy_type?.replace(/_/g, ' ')}
                    </p>
                  </div>
                </div>

                {/* Warning */}
                <div className="flex gap-2 items-start p-2.5 mb-4 bg-blue-50 rounded-lg border border-blue-200">
                  <AlertCircle className="flex-shrink-0 w-4 h-4 text-blue-600 mt-0.5" />
                  <p className="text-xs sm:text-sm text-blue-800">
                    Once approved, this policy will be visible to all users and can be purchased.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-2 sm:gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleApprove}
                    disabled={isSubmitting}
                    className="flex flex-1 justify-center items-center px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base font-medium text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="w-5 h-5 rounded-full border-2 animate-spin border-white/30 border-t-white" />
                    ) : (
                      <>
                        <CheckCircle className="mr-1.5 w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="hidden sm:inline">Approve Policy</span>
                        <span className="sm:hidden">Approve</span>
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
